/* eslint-disable prettier/prettier */
import React, { FC, useCallback, useEffect } from 'react'

import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Fab,
  Grid,
  InputAdornment,
  Stack,
  SxProps,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import {
  Add,
  AssignmentTurnedIn,
  Healing,
  Pets,
  ReportGmailerrorredOutlined,
  Search as SearchIcon,
  Tune,
  Transgender,
} from '@mui/icons-material'
import dayjs from 'dayjs'

import {
  AdopterDto,
  AdopterDtoPreferenceSex,
  AdopterDtoPreferenceSterilizedOrNeutered,
  AdopterDtoProfileHasBeenAccepted,
  AdoptionAttemptDto,
  AdoptionAttemptDtoStatus,
  AdoptionAttemptDtoSterilizationDepositStatus,
  AnimalDtoSpecies,
  AnimalInsightsDtoType,
} from '../../interactors/gen/backendClient'
import { CircularProgressPanel } from '../common/CircularProgressPanel'

import { useMediaQuery } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { accountCanEdit } from '../../domain/Account/accountCanEdit'
import {
  getColorForAdopterProfileHasBeenAccepted,
  getReadableAdopterFieldLabel,
  getReadableAdopterPreferenceSex,
  getReadableAdopterPreferenceSterilized,
  getReadableAdopterProfileHasBeenAccepted,
} from '../../domain/Adopters/AccountDisplay'
import { searchAdopters } from '../../domain/Adopters/searchAdopters'
import { sortAdopters } from '../../domain/Adopters/sortAdopters'
import { getReadableSpecies } from '../../domain/Animal/AnimalDisplay'
import { useAccountsStore } from '../../store/AccountsStore'
import { useAdoptersStore } from '../../store/AdoptersStore'
import { useFetchOrRefreshAdoptersOnMount } from '../../store/useFetchOrRefreshAdoptersOnMount'
import { getAnimalProfilePhotoSrc } from '../../utils/S3-links'
import { getAdoptionAttemptStatus } from '../../utils/getAdoptionAttemptStatus'
import { setOpacity } from '../../utils/setOpacity'
import { sortAlphabetically } from '../../utils/sort'
import { HtmlTooltip } from '../Animals/AnimalsTableItem'
import { FeaturesList } from '../Association/Functionalities/FeaturesList'
import { PATHS } from '../PATHS'
import { FilterList } from '../common/FilterList'
import { FilterListItem } from '../common/FilterListItem'
import { GettingStartedPanel } from '../common/GettingStartedPanel'
import { InfoBox } from '../common/InfoBox'
import { OverviewFilterDialog, OverviewFilterSection } from '../common/OverviewFilterDialog'
import { addPrimaryFabSx } from '../common/addPrimaryCtaSx'
import { getSoftChipSx } from '../common/chipStyles'
import {
  overviewActionButtonSx,
  overviewFilterCountBadgeSx,
  overviewTabSx,
  overviewTabsSx,
} from '../common/overviewStyles'
import { searchFieldSx } from '../common/searchFieldSx'
import { blue, colors, spacingItem, theme } from '../theme'
import { AdopterListCard } from './AdopterListCard'
import { debounce } from 'lodash'
import { StripeConnectButton } from './StripeConnectButton'

type Tabs =
  | 'all'
  | 'new'
  | 'to-do'
  | 'waiting'
  | 'to-match'
  | 'settings'
  | 'on-going-no-action'
  | 'done'
  | 'sterilization-deposit'

export const AdoptersListScreen: FC = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const getTabFromSearch = (): Tabs => {
    const tab = queryParams.get('tab') as Tabs | null

    switch (tab) {
      case 'all':
      case 'new':
      case 'to-do':
      case 'to-match':
      case 'settings':
      case 'on-going-no-action':
      case 'done':
      case 'sterilization-deposit':
        return tab
      default:
        return 'to-do'
    }
  }
  const [adopters, setAdopters] = React.useState<AdopterDto[]>([])
  const [selectedTab, setSelectedTab] = React.useState<Tabs>(getTabFromSearch())
  const [selectedSpecies, setSelectedSpecies] = React.useState<AnimalDtoSpecies[]>([])
  const [selectedSexs, setSelectedSexs] = React.useState<AdopterDtoPreferenceSex[]>([])
  const [selectedSterilisations, setSelectedSterilisations] = React.useState<
    AdopterDtoPreferenceSterilizedOrNeutered[]
  >([])
  const [selectedProfileHasBeenAccepted, setSelectedProfileHasBeenAccepted] = React.useState<
    AdopterDtoProfileHasBeenAccepted[]
  >([])
  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false)
  const activeFiltersCount = [
    selectedSpecies.length > 0,
    selectedSexs.length > 0,
    selectedSterilisations.length > 0,
    selectedProfileHasBeenAccepted.length > 0,
  ].filter(Boolean).length

  const navigate = useNavigate()

  useFetchOrRefreshAdoptersOnMount()

  const adoptersStore = useAdoptersStore()
  const accountsStore = useAccountsStore()

  const association = accountsStore.connectedCustomer
  const hasAnyAdopter = adoptersStore.adopters.some((adopter) => !adopter.isDeleted)
  const hasTemplate = association?.templates && association.templates.length > 0 ? true : false
  const hasEmailTemplate = association?.emailTemplate !== undefined
  const toDoCount = adoptersStore.adopters.filter(
    (adopter) =>
      !adopter.isDeleted &&
      adopter.adoptionAttempts?.some((attempt: AdoptionAttemptDto) => attempt.insights && attempt.insights.length > 0)
  ).length

  useEffect(() => {
    setSelectedTab(getTabFromSearch())
  }, [location.search])

  useEffect(() => {
    const isNotDeleted = (adopter: AdopterDto) => !adopter.isDeleted
    const hasAdoptionWithInsight = (adopter: AdopterDto) => {
      return adopter.adoptionAttempts?.some(
        (attempt: AdoptionAttemptDto) => attempt.insights && attempt.insights.length > 0
      )
    }
    const isWaitingOnAdopterWithoutInsights = (attempt: AdoptionAttemptDto) =>
      (attempt.insights?.length ?? 0) < 1 && !attempt.done
    const hasOverdueSterilizationDeposit = (adopter: AdopterDto) => {
      return adopter.adoptionAttempts?.some((attempt: AdoptionAttemptDto) => {
        const sterilization = attempt.sterilization
        if (!sterilization?.needed || !sterilization.date) {
          return false
        }
        const isPast = dayjs(sterilization.date).isBefore(dayjs(), 'day')
        const depositStatus = sterilization.depositStatus
        const isNotRefunded = depositStatus !== AdoptionAttemptDtoSterilizationDepositStatus.Refunded
        return isPast && isNotRefunded
      })
    }

    switch (selectedTab) {
      case 'all':
        setAdopters(adoptersStore.adopters.filter(isNotDeleted))
        break
      case 'new':
        setAdopters(
          adoptersStore.adopters.filter(
            (adopter) =>
              isNotDeleted(adopter) && adopter.profileHasBeenAccepted === AdopterDtoProfileHasBeenAccepted.Waiting
          )
        )
        break
      case 'to-do':
        setAdopters(
          adoptersStore.adopters.filter((adopter) => isNotDeleted(adopter) && hasAdoptionWithInsight(adopter))
        )
        break
      case 'on-going-no-action':
        setAdopters(
          adoptersStore.adopters.filter(
            (adopter) =>
              isNotDeleted(adopter) &&
              adopter.adoptionAttempts?.some((attempt) => isWaitingOnAdopterWithoutInsights(attempt))
          )
        )
        break
      case 'done':
        setAdopters(
          adoptersStore.adopters.filter(
            (adopter) => isNotDeleted(adopter) && adopter.adoptionAttempts?.some((attempt) => attempt.done)
          )
        )
        break
      case 'sterilization-deposit':
        setAdopters(
          adoptersStore.adopters.filter((adopter) => isNotDeleted(adopter) && hasOverdueSterilizationDeposit(adopter))
        )
        break
      case 'to-match':
        setAdopters(
          adoptersStore.adopters.filter(
            (adopter) =>
              isNotDeleted(adopter) &&
              adopter.profileHasBeenAccepted === AdopterDtoProfileHasBeenAccepted.Accepted &&
              (adopter.adoptionAttempts?.length === 0 || adopter.adoptionAttempts === undefined)
          )
        )
        break
      case 'settings':
        break
      default:
        throw new Error(`selectedTab ${selectedTab} not supported`)
    }
  }, [adoptersStore.adopters, selectedTab])

  const handleTabChange = (_: React.SyntheticEvent | null, newValue: Tabs) => {
    navigate(`/suivi-adoptant?tab=${newValue}`)
    setSelectedTab(newValue)
  }

  const [searchQuery, setSearchQuery] = React.useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState(queryParams.get('search') || '')
  const resetFilters = () => {
    setSelectedSpecies([])
    setSelectedSexs([])
    setSelectedSterilisations([])
    setSelectedProfileHasBeenAccepted([])
  }

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setSearchQuery(e.target.value)
  }

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      setDebouncedSearchQuery(query)
      if (query !== '' && query != null && selectedTab !== 'all') {
        handleTabChange(null, 'all') // If you still want to auto-switch tab
      }
    }, 300), // reduce from 500ms -> 300ms for a snappier feel
    []
  )

  // As the user types, we call the stable 'debouncedSearch' function
  useEffect(() => {
    debouncedSearch(searchQuery)
    return () => {
      debouncedSearch.cancel()
    }
  }, [searchQuery, debouncedSearch])

  const account = accountsStore.connectedAccount

  const species = accountsStore.connectedCustomer?.preferences.species

  const numberOfWaitingProfiles = adoptersStore.adopters.filter(
    (adopter) => adopter.profileHasBeenAccepted === AdopterDtoProfileHasBeenAccepted.Waiting
  ).length

  const profileHasBeenAcceptedCustomOrder = [
    AdopterDtoProfileHasBeenAccepted.FormToSent,
    AdopterDtoProfileHasBeenAccepted.Waiting,
    AdopterDtoProfileHasBeenAccepted.FormAccepted,
    AdopterDtoProfileHasBeenAccepted.Accepted,
    AdopterDtoProfileHasBeenAccepted.FormSent,
    AdopterDtoProfileHasBeenAccepted.FormRefused,
    AdopterDtoProfileHasBeenAccepted.Refused,
    AdopterDtoProfileHasBeenAccepted.Blacklisted,
  ]

  const FiltersBar = () => {
    return (
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
        <OverviewFilterSection>
          <FilterList label="Statut de l'adoptant" icon={<AssignmentTurnedIn />}>
            {profileHasBeenAcceptedCustomOrder.map((profil) => {
              const isSelected = selectedProfileHasBeenAccepted.includes(profil)

              return (
                <FilterListItem
                  key={profil}
                  label={`${getReadableAdopterProfileHasBeenAccepted(profil)} ${
                    profil === AdopterDtoProfileHasBeenAccepted.Waiting ? ': ' + numberOfWaitingProfiles : ''
                  }`}
                  isSelected={isSelected}
                  toggleSelected={() => {
                    if (isSelected) {
                      setSelectedProfileHasBeenAccepted(selectedProfileHasBeenAccepted.filter((p) => p !== profil))
                    } else {
                      setSelectedProfileHasBeenAccepted([...selectedProfileHasBeenAccepted, profil])
                    }
                  }}
                />
              )
            })}
          </FilterList>
        </OverviewFilterSection>

        <OverviewFilterSection>
          <Typography variant="h6" gutterBottom sx={spacingItem}>
            Préférences des adoptants
          </Typography>

          {species && species.length > 1 && (
            <FilterList label="Espèces" icon={<Pets />}>
              {species?.map((specie) => {
                const isSelected = selectedSpecies.includes(specie)

                return (
                  <FilterListItem
                    key={specie}
                    label={getReadableSpecies(specie)}
                    isSelected={isSelected}
                    toggleSelected={() => {
                      if (isSelected) {
                        setSelectedSpecies(selectedSpecies.filter((s) => s !== specie))
                      } else {
                        setSelectedSpecies([...selectedSpecies, specie])
                      }
                    }}
                  />
                )
              })}
            </FilterList>
          )}

          <FilterList label="Sexe" icon={<Transgender />}>
            {Object.values(AdopterDtoPreferenceSex).map((sex) => {
              const isSelected = selectedSexs.includes(sex)

              return (
                <FilterListItem
                  key={sex}
                  label={getReadableAdopterPreferenceSex(sex)}
                  isSelected={isSelected}
                  toggleSelected={() => {
                    if (isSelected) {
                      setSelectedSexs(selectedSexs.filter((s) => s !== sex))
                    } else {
                      setSelectedSexs([...selectedSexs, sex])
                    }
                  }}
                />
              )
            })}
          </FilterList>

          <FilterList label="Stérilisation" icon={<Healing />}>
            {Object.values(AdopterDtoPreferenceSterilizedOrNeutered).map((sterilisation) => {
              const isSelected = selectedSterilisations.includes(sterilisation)

              return (
                <FilterListItem
                  key={sterilisation}
                  label={getReadableAdopterPreferenceSterilized(sterilisation)}
                  isSelected={isSelected}
                  toggleSelected={() => {
                    if (isSelected) {
                      setSelectedSterilisations(selectedSterilisations.filter((s) => s !== sterilisation))
                    } else {
                      setSelectedSterilisations([...selectedSterilisations, sterilisation])
                    }
                  }}
                />
              )
            })}
          </FilterList>
        </OverviewFilterSection>
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          minHeight: 0,
          padding: 0,
          backgroundColor: 'transparent',
          boxSizing: 'border-box',
          marginBottom: 0,
          maxHeight: '100%',
        }}
      >
        {adoptersStore.isLoading ? <CircularProgressPanel /> : <></>}

        <Card
          sx={{
            ...spacingItem,
            marginTop: 0,
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={overviewTabsSx}
          >
            <Tab label="Tous" value="all" sx={overviewTabSx} />
            <Tab
              label={
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8 }}>
                  <Box component="span">À faire</Box>
                  {toDoCount > 0 ? (
                    <Box
                      component="span"
                      sx={{
                        minWidth: 20,
                        height: 20,
                        px: 0.7,
                        borderRadius: '999px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#F6BE3A',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {toDoCount}
                    </Box>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '999px',
                        backgroundColor: '#7FB38A',
                        boxShadow: '0 0 0 3px rgba(127, 179, 138, 0.12)',
                      }}
                    />
                  )}
                </Box>
              }
              value="to-do"
              sx={overviewTabSx}
            />
            <Tab label="En attente adoptant" value="on-going-no-action" sx={overviewTabSx} />
            <Tab label="Stérilisation dépassée" value="sterilization-deposit" sx={overviewTabSx} />
            <Tab label="Passées" value="done" sx={overviewTabSx} />
            <Tab label="Profils à matcher" value="to-match" sx={overviewTabSx} />
            <Tab label="Paramétrage de la procédure d'adoption" value="settings" sx={overviewTabSx} />
          </Tabs>
          {selectedTab !== 'settings' && (
            <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: 2.5 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1, mb: 1.5 }}
              >
                <TextField
                  id="name-input"
                  placeholder="Rechercher dans les adoptants"
                  type="text"
                  fullWidth
                  value={searchQuery}
                  onChange={onChangeSearch}
                  sx={searchFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={
                    activeFiltersCount > 0 ? (
                      <Box component="span" sx={overviewFilterCountBadgeSx}>
                        {activeFiltersCount}
                      </Box>
                    ) : (
                      <Tune fontSize="small" />
                    )
                  }
                  onClick={() => setIsFiltersOpen(true)}
                  sx={{ ...overviewActionButtonSx, flexShrink: 0, height: 40, py: 0 }}
                >
                  Filtres
                </Button>
                {activeFiltersCount > 0 && (
                  <Button
                    variant="text"
                    onClick={resetFilters}
                    sx={{
                      display: { xs: 'none', sm: 'inline-flex' },
                      textTransform: 'none',
                      color: '#7A8197',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',
                      minWidth: 'auto',
                      px: 1,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: colors.cello,
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Reinitialiser les filtres
                  </Button>
                )}
              </Box>
            </Box>
          )}
          <Box
            sx={{
              width: '100%',
              flexGrow: 1,
              minHeight: 0,
              boxSizing: 'border-box',
              overflow: 'auto',
              paddingBottom: 17, // HACK: To avoid the last row to be hidden
            }}
          >
            {selectedTab === 'settings' ? (
              <Grid container spacing={3} sx={{ paddingX: 3 }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                    <Typography variant="h1" sx={{ color: blue, fontSize: '1.5rem', fontWeight: 700 }}>
                      Le parcours d’adoption et carnet de santé Petso
                    </Typography>
                    <Typography sx={{ color: colors.blueSky }}>Mode d’emploi</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FeaturesList title="1. Créer vos modèles">
                    <ol style={{ paddingLeft: '60px', color: colors.blueSky, fontSize: '15px' }}>
                      <li>Importer un de vos modèles PDF existants</li>
                      <li>Définissez les zones de signatures et les champs à remplir</li>
                      <li>Enregistrer votre modèle</li>
                      <li>
                        A chaque demande d’adoption Petso génère automatiquement vos documents sur la base du modèle
                        choisi
                      </li>
                    </ol>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: '32px' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'start',
                          alignItems: 'center',
                          textAlign: 'left',
                          backgroundColor: 'rgba(247, 248, 255, 1)',
                          borderRadius: '7px',
                          paddingX: '1rem',
                          paddingY: '0.5rem',
                        }}
                      >
                        <ReportGmailerrorredOutlined sx={{ color: colors.blueSky, fontSize: '3rem' }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', ml: 2 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.cello,
                              fontSize: '11px',
                            }}
                          >
                            Si vous préferez assurer la signature des documents par vos propres moyen vous pouvez
                            séléctionner l’option “remis en main propre” lors de la création d’une procédure d’adoption.
                            <br />
                            L’étape 1 &quot;Créer vos modèles&quot; n&apos;est alors pas nécessaire.
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        sx={{ marginTop: 2, py: 1 }}
                        onClick={() => navigate('/association?tab=templates')}
                      >
                        {hasTemplate ? 'Ajouter ou modifier mes modèles' : 'Créer mon modèle de certificat et contrat'}
                      </Button>
                    </Box>
                  </FeaturesList>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'end',
                      alignItems: 'flex-end',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ color: 'black', textAlign: 'right', width: '100%', fontSize: '14px' }}
                    >
                      Besoin d’aide ? <span style={{ textDecoration: 'underline' }}>Consulter le tutoriel !</span>
                    </Typography>
                    <iframe
                      width="560"
                      height="315"
                      src="https://www.youtube.com/embed/1oO1jA-VLNo?si=EjP_wX0pISk9wFLq"
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    ></iframe>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FeaturesList title="2. Enregistrez votre mail adressé à l’adoptant">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: '32px', mt: 2, maxWidth: '100%' }}>
                      <img
                        src="https://i.imgur.com/5obWTNY.png"
                        alt="Capture d'écran de l'interface email"
                        width={555}
                        height={292}
                        style={{ maxWidth: '100%' }}
                      />
                    </Box>
                  </FeaturesList>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ color: colors.blueSky, fontSize: '15px', mt: 8 }}>
                    Pour chaque adoption, un mail est envoyé à l’adoptant lui permettant d’accéder à un espace dédié
                    pour signer les documents et si nécessaire régler les frais d’adoption.
                    <br /> <br />
                    Pour des raisons techniques, ce mail est envoyé par l’adresse contact@petso.fr
                    <br /> <br />
                    <b>Vous pouvez pour autant le signer et le personnaliser selon vos souhaits.</b>
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: hasEmailTemplate ? theme.palette.success.main : blue,
                      fontSize: '15px',
                      fontWeight: 700,
                      mt: 4,
                    }}
                  >
                    {hasEmailTemplate
                      ? 'Votre mail est bien enregistré !'
                      : 'Une fois finalisé pensez à bien enregistrer !'}
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    sx={{ marginTop: 1, py: 1 }}
                    onClick={() => navigate('./email-template')}
                  >
                    {hasEmailTemplate ? 'Modifier' : 'Configurer'} mon mail
                  </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FeaturesList title="3. Signature et validation des documents">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: '32px' }}>
                      <Typography variant="body1" sx={{ color: colors.blueSky, fontSize: '15px', mt: 2 }}>
                        Une fois la procédure d’adoption crée, l’adoptant accède à son espace pour signer les documents
                        et vous les renvoyer.
                        <br /> <br />
                        Vous êtes notifiés de la réception et en mesure de les valider/refuser-redemander la signature.
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'start',
                          alignItems: 'center',
                          textAlign: 'left',
                          backgroundColor: 'rgba(247, 248, 255, 1)',
                          borderRadius: '7px',
                          paddingX: '1rem',
                          paddingY: '0.5rem',
                        }}
                      >
                        <ReportGmailerrorredOutlined sx={{ color: colors.blueSky, fontSize: '3rem' }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', ml: 2 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.cello,
                              fontSize: '11px',
                            }}
                          >
                            Vous devez débloquer le contrat pour qu’il soit accéssible par l’adoptant pour signature,
                            conformement au 8 jours de délai légal
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </FeaturesList>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FeaturesList title="4. Réglement des frais d’adoption en ligne">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: '32px', width: '100%' }}>
                      <Typography variant="body1" sx={{ color: colors.blueSky, fontSize: '15px', mt: 2 }}>
                        Nous proposons une fonctionnalité de réglement de frais d’adoption en ligne sans frais.
                        <br /> <br />
                        Une fois un réglement effectué, le statut de paiement est automatiquement validée dans votre
                        procédure.
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'start',
                          alignItems: 'center',
                          textAlign: 'left',
                          backgroundColor: 'rgba(247, 248, 255, 1)',
                          borderRadius: '7px',
                          paddingX: '1rem',
                          paddingY: '0.5rem',
                        }}
                      >
                        <ReportGmailerrorredOutlined sx={{ color: colors.blueSky, fontSize: '3rem' }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', ml: 2 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.cello,
                              fontSize: '11px',
                            }}
                          >
                            Le réglement en ligne représente un coût. Nous sollicitons les adoptants via un système de
                            pourboire afin de les compenser, rien de plus !
                          </Typography>
                        </Box>
                      </Box>
                      <StripeConnectButton />
                      {accountsStore.connectedCustomer?.stripeAccountId && (
                        <Button
                          variant="contained"
                          color="secondary"
                          fullWidth
                          onClick={() => window.open('https://dashboard.stripe.com/login', '_blank')}
                        >
                          Accéder à mon espace Stripe Connect
                        </Button>
                      )}
                    </Box>
                  </FeaturesList>
                </Grid>
                <Grid item xs={12} md={12}>
                  <FeaturesList title="5. Adoption terminée ! Le carnet de santé digitale pour chaque adoptant">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: '32px' }}>
                      <Typography variant="body1" sx={{ color: colors.blueSky, fontSize: '15px', mt: 2 }}>
                        Une fois adopté, l’adoptant conserve gratuitement l’espace de son animal qu’il pourra compléter
                        dans le temps
                        <br />
                        Transmettez et échangez depuis Petso avec chaque adoptants :
                      </Typography>
                      <ul style={{ color: colors.blueSky, fontSize: '15px', marginTop: 0 }}>
                        <li>Antécédents de santé & rappels des prochains rendez-vous (vermifuges, vaccins...)</li>
                        <li>
                          Date de stérilisation prévue (si demandée). L’adoptant pourra vous envoyer le justificatif
                        </li>
                        <li>Transmission des documents (certificat vétérinaire...)</li>
                      </ul>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'start',
                          alignItems: 'center',
                          textAlign: 'left',
                          backgroundColor: 'rgba(105, 172, 115, 0.4)',
                          borderRadius: '7px',
                          paddingX: '1rem',
                          paddingY: '0.5rem',
                        }}
                      >
                        <ReportGmailerrorredOutlined sx={{ color: 'rgba(107, 107, 107, 1)', fontSize: '3rem' }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', ml: 2 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'rgba(107, 107, 107, 1)',
                              fontSize: '11px',
                            }}
                          >
                            Dans le cadre de notre collaboration, nous offrons à chaque adoptant une semaine gratuite de
                            nos services et recommandations auprès de notre séléction partenaire pour favoriser
                            l’arrivée et le bien-être de l’animal !
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </FeaturesList>
                </Grid>
              </Grid>
            ) : adoptersStore.isLoading ? (
              <CircularProgressPanel />
            ) : !hasAnyAdopter ? (
              <GettingStartedPanel />
            ) : (
              <SearchResults
                members={adopters}
                searchQuery={debouncedSearchQuery}
                filters={{ selectedSpecies, selectedSexs, selectedSterilisations, selectedProfileHasBeenAccepted }}
              />
            )}
          </Box>

          <OverviewFilterDialog open={isFiltersOpen} onClose={() => setIsFiltersOpen(false)} title="Filtres adoptants">
            <FiltersBar />
          </OverviewFilterDialog>
        </Card>
      </Box>

      {account && accountCanEdit(account) && (
        <Box sx={{ position: 'absolute', right: 2, bottom: 0, margin: 2 }}>
          <Fab
            color="primary"
            variant="extended"
            onClick={() => navigate(PATHS.ajouterAdoptant.absolute)}
            sx={addPrimaryFabSx}
          >
            <Add sx={{ mr: 1 }} />
            Ajouter un Adoptant
          </Fab>
        </Box>
      )}
    </>
  )
}

/** Rows mounted at once; more are added as the bottom of the list is approached. */
const ROWS_PER_BATCH = 40

type SortDirection = 'asc' | 'desc'
type SortColumn = 'firstName' | 'lastName' | 'email' | 'profileHasBeenAccepted' | 'phoneNumber'

type SearchResultsProps = {
  members: Array<AdopterDto>
  searchQuery: string
  filters?: {
    selectedSpecies: AnimalDtoSpecies[]
    selectedSexs: AdopterDtoPreferenceSex[]
    selectedSterilisations: AdopterDtoPreferenceSterilizedOrNeutered[]
    selectedProfileHasBeenAccepted: AdopterDtoProfileHasBeenAccepted[]
  }
}

const SearchResults: React.FC<SearchResultsProps> = ({ members, searchQuery, filters }) => {
  const [sortColumn, setSortColumn] = React.useState<SortColumn>('profileHasBeenAccepted')
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc')
  const [expandCardIndex, setExpandCardIndex] = React.useState<number | null>(null)
  const isMobile = useMediaQuery('(max-width:600px)')
  const handleSort = (column: SortColumn, columnDefaultDirection: SortDirection) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortDirection(columnDefaultDirection)
    }

    setSortColumn(column)
  }
  // Keyed on the contents rather than the object: the parent rebuilds `filters` on every render,
  // so depending on its identity would defeat the memo entirely.
  const filtersKey = JSON.stringify([
    filters?.selectedSpecies ?? [],
    filters?.selectedSexs ?? [],
    filters?.selectedSterilisations ?? [],
    filters?.selectedProfileHasBeenAccepted ?? [],
  ])

  const searchResult = React.useMemo(
    () =>
      searchAdopters(members, searchQuery)
    .filter((member) => {
      if (!filters) return true
      if (!filters.selectedSpecies.length) {
        return true
      }
      return member.preferences.species
        ? member.preferences.species.some((specie) => filters.selectedSpecies.includes(specie))
        : false
    })
    .filter((member) => {
      if (!filters) return true
      if (!filters.selectedSexs.length) {
        return true
      }
      return member.preferences.sex ? filters.selectedSexs.includes(member.preferences.sex) : false
    })
    .filter((member) => {
      if (!filters) return true
      if (!filters.selectedSterilisations.length) {
        return true
      }
      return member.preferences.sterilizedOrNeutered
        ? filters.selectedSterilisations.includes(member.preferences.sterilizedOrNeutered)
        : false
    })
    .filter((member) => {
      if (!filters) return true
      if (!filters.selectedProfileHasBeenAccepted.length) {
        return true
      }
      return member.profileHasBeenAccepted
        ? filters.selectedProfileHasBeenAccepted.includes(member.profileHasBeenAccepted)
        : false
    })
    .sort((a, b) => {
      switch (sortColumn) {
        case 'firstName':
          return sortAlphabetically(a, b, sortDirection, 'firstName')
        case 'lastName':
          return sortAlphabetically(a, b, sortDirection, 'lastName')
        case 'email':
          return sortAlphabetically(a, b, sortDirection, 'email')
        case 'profileHasBeenAccepted':
          return sortAdopters(a, b)
        case 'phoneNumber':
          return sortAlphabetically(a, b, sortDirection, 'phoneNumber', 'firstName')
        default:
          throw new Error(`sortColumn ${sortColumn} not supported`)
      }
    }),
    [members, searchQuery, filtersKey, sortColumn, sortDirection]
  )

  // An organization can have well over a thousand adopters, and every row carries avatars and a
  // chip per ongoing adoption. Mounting them all is what made the screen slow once the data itself
  // became fast, so rows are added as they are scrolled towards. The search above still runs over
  // every adopter, so nothing becomes unfindable.
  const [visibleCount, setVisibleCount] = React.useState(ROWS_PER_BATCH)
  const sentinelRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    setVisibleCount(ROWS_PER_BATCH)
  }, [members, searchQuery, filtersKey, sortColumn, sortDirection])

  const hasMore = visibleCount < searchResult.length

  React.useEffect(() => {
    const node = sentinelRef.current
    if (!hasMore || !node) {
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleCount((count) => count + ROWS_PER_BATCH)
        }
      },
      // Start loading before the sentinel is actually on screen, so scrolling stays smooth.
      { rootMargin: '600px' }
    )
    observer.observe(node)

    return () => observer.disconnect()
  }, [hasMore, visibleCount])

  const visibleMembers = React.useMemo(() => searchResult.slice(0, visibleCount), [searchResult, visibleCount])

  return !isMobile ? (
    // Desktop version
    <Table stickyHeader aria-label="simple table">
      <TableHead>
        <TableRow>
          <TableCell>
            <TableSortLabel
              active={sortColumn === 'firstName'}
              direction={sortDirection}
              onClick={() => {
                handleSort('firstName', 'asc')
              }}
            >
              {getReadableAdopterFieldLabel('firstName')}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel
              active={sortColumn === 'lastName'}
              direction={sortDirection}
              onClick={() => {
                handleSort('lastName', 'asc')
              }}
            >
              {getReadableAdopterFieldLabel('lastName')}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel
              active={sortColumn === 'email'}
              direction={sortDirection}
              onClick={() => {
                handleSort('email', 'asc')
              }}
            >
              {getReadableAdopterFieldLabel('email')}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel
              active={sortColumn === 'phoneNumber'}
              direction={sortDirection}
              onClick={() => {
                handleSort('phoneNumber', 'asc')
              }}
            >
              Adoption(s) en cours
            </TableSortLabel>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody sx={{ height: '100%' }}>
        {visibleMembers.map((member) => (
          <AdopterListItem key={member.id} member={member} />
        ))}
      </TableBody>
      {hasMore && (
        <caption style={{ padding: 0 }}>
          <Box ref={sentinelRef} sx={{ height: '1px' }} />
        </caption>
      )}
    </Table>
  ) : (
    // Mobile version
    <Stack spacing={1.5} marginTop="10px" marginBottom="80px" marginX="15px">
      {visibleMembers.map((member, index) => (
        <AdopterListCard
          key={member.id}
          member={member}
          index={index}
          expandCardIndex={expandCardIndex}
          setExpandCardIndex={setExpandCardIndex}
        />
      ))}
      {hasMore && <Box ref={sentinelRef} sx={{ height: '1px' }} />}
    </Stack>
  )
}

type ItemProps = {
  member: AdopterDto
}

const AdopterListItemRow: React.FC<ItemProps> = ({ member }) => {
  const navigate = useNavigate()
  const visibleAttempts = member.adoptionAttempts?.slice(0, 6) || []
  const remainingAttemptsCount = Math.max((member.adoptionAttempts?.length || 0) - visibleAttempts.length, 0)

  const handleClick = () => {
    navigate(`${member.id}`)
  }

  return (
    <TableRow
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        height: '100%',
        '&:hover': {
          backgroundColor: 'rgba(23, 31, 52, 0.05)',
        },
      }}
    >
      <TableCell component="th" scope="row" sx={{ fontWeight: 700 }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {member.profileHasBeenAccepted && (
            <Tooltip title={getReadableAdopterProfileHasBeenAccepted(member.profileHasBeenAccepted)}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  width: 16,
                  height: 16,
                  borderRadius: '999px',
                  backgroundColor: setOpacity(
                    getColorForAdopterProfileHasBeenAccepted(member.profileHasBeenAccepted),
                    0.9
                  ),
                  boxShadow: 'inset 0 0 0 1px rgba(23, 31, 52, 0.12)',
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          )}
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {member.firstName}
          </Box>
        </Box>
      </TableCell>
      <TableCell component="th" scope="row">
        {member.lastName}
      </TableCell>
      <TableCell component="th" scope="row">
        {member.email}
      </TableCell>

      <TableCell component="th" scope="row">
        <Box
          sx={{
            display: 'inline-flex',
            gap: 0.9,
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            width: '100%',
            maxWidth: 960,
            py: 0.25,
          }}
        >
          {visibleAttempts.map((attempt) => {
            return (
              <AdoptionAttemptChip
                key={attempt.id}
                member={member}
                attempt={attempt as AdoptionAttemptWithAnimal}
                avatarSx={animalAvatarSx}
                textSx={adoptionChipTextSx}
              />
            )
          })}
          {remainingAttemptsCount > 0 && (
            <Box
              component="span"
              sx={{
                ...getSoftChipSx(colors.cello, {
                  height: 28,
                  fontSize: 11.5,
                  labelWeight: 600,
                }),
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.3,
                color: colors.cello,
                backgroundColor: 'rgba(105, 141, 172, 0.1)',
                borderColor: 'rgba(105, 141, 172, 0.24)',
              }}
            >
              +{remainingAttemptsCount}
            </Box>
          )}
        </Box>
      </TableCell>
    </TableRow>
  )
}

/** A row only re-renders when its own adopter changes, not when a sibling or the parent does. */
const AdopterListItem = React.memo(AdopterListItemRow)

export type AdoptionAttemptWithAnimal = AdoptionAttemptDto & {
  animal: {
    name: string
    pictureUrl: string
  }
}

type ChipProps = {
  member: AdopterDto
  attempt: AdoptionAttemptWithAnimal
  avatarSx: SxProps
  textSx: SxProps
}

// AdoptionAttemptsChip props

const animalAvatarSx = {
  height: '34px !important',
  width: '34px !important',
  ml: '-1px !important',
  mt: '-1px !important',
}
const adoptionChipTextSx = {
  fontSize: 11.5,
  height: 34,
  pl: 0,
  gap: 0,
}

export const AdoptionAttemptChip: React.FC<ChipProps> = ({ member, attempt, avatarSx, textSx }) => {
  const navigate = useNavigate()

  const order: Array<AnimalInsightsDtoType> = ['info', 'error', 'warning']
  const insights = attempt.insights || []
  const insightType = insights[0]?.type
  const insightsCount = insights.length
  const hasSterilizationCertificateToValidate =
    Boolean(attempt.sterilization?.file) && !attempt.sterilization?.validated
  const statusLabel = getAdoptionAttemptStatus(attempt)
  const isSuspended =
    attempt.status === AdoptionAttemptDtoStatus.SuspendedByCustomer ||
    attempt.status === AdoptionAttemptDtoStatus.SuspendedByUser
  const shouldShowRecapAlerts = !isSuspended
  const shouldShowTooltip =
    shouldShowRecapAlerts && (Boolean(insightType) || hasSterilizationCertificateToValidate)
  const statusTone = isSuspended
    ? '#E8C7D3'
    : attempt.done || attempt.archived
    ? '#A5C7AD'
    : statusLabel === "En attente de l'adoptant"
    ? '#A8C9EA'
    : statusLabel === 'Adoption à finaliser'
    ? '#EFD79A'
    : statusLabel === 'Envoyer le contrat'
    ? '#BCD6EE'
    : statusLabel === 'Valider la réception du paiement' || statusLabel === 'Paiement en attente'
    ? '#F1C774'
    : '#D3D9E5'

  return (
    <HtmlTooltip
      onClick={(e) => e.stopPropagation()}
      title={
        shouldShowTooltip ? (
          <Box>
            {/* show animal name at top of the tooltip */}
            <Typography variant="subtitle2" gutterBottom>
              {attempt.animal.name}
            </Typography>
            {hasSterilizationCertificateToValidate && (
              <InfoBox
                messageType="warning"
                content="Certificat de stérilisation transmis par l'adoptant"
                sx={{ mb: insights.length > 0 ? 1 : 0 }}
              />
            )}
            {insights
              .sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type))
              .map((insight, i) => (
                <InfoBox key={i} messageType={insight.type} content={insight.message} sx={{ mb: 1 }} />
              ))}
          </Box>
        ) : null
      }
    >
      <Chip
        clickable
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
          navigate(
            `${PATHS.suiviAdoptant.absolute}/${member.id}?tab=${getTabForAdoptionAttemptStatus(
              attempt
            )}-adoption&attemptId=${attempt.id}`
          )
        }}
        avatar={
          <Avatar alt={attempt.animal.name} src={getAnimalProfilePhotoSrc(attempt.animal.pictureUrl)} sx={avatarSx} />
        }
        // two-line label: name above status
        label={
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            justifyContent="center"
            height="100%"
            sx={{ py: 0.25 }}
          >
            <Typography variant="body2" fontWeight={700} noWrap lineHeight={1.08} sx={{ fontSize: 12.5 }}>
              {attempt.animal.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#616E87', fontSize: 11.2, fontWeight: 500 }}
              lineHeight={1.08}
              noWrap
            >
              {statusLabel}
            </Typography>
          </Box>
        }
        variant="outlined"
        sx={{
          ...textSx,
          minWidth: 0,
          maxWidth: 286,
          borderRadius: '999px',
          backgroundColor: setOpacity(statusTone, 0.16),
          borderColor: setOpacity(statusTone, 0.34),
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: `${setOpacity(statusTone, 0.22)} !important`,
            borderColor: setOpacity(statusTone, 0.44),
          },
          position: 'relative',
          '& .MuiChip-avatar': {
            ...(avatarSx as Record<string, unknown>),
            border: '1px solid rgba(228, 232, 242, 0.95)',
          },
          '& .MuiChip-label': {
            minWidth: 0,
            pr: 1.25,
          },
          '&::before': {
            content: `"${insightsCount}"`,
            position: 'absolute',
            display: shouldShowRecapAlerts && insightsCount > 0 ? 'flex' : 'none',
            justifyContent: 'center',
            alignItems: 'center',
            top: 2,
            right: 2,
            backgroundColor: (theme) => (insightType ? theme.palette[insightType].main : theme.palette.warning.main),
            color: 'white',
            fontWeight: 700,
            borderRadius: '50%',
            width: 21,
            height: 21,
            fontSize: 11,
            boxShadow: '0 1px 4px rgba(32, 56, 100, 0.15)',
          },
          '&::after': {
            content: '"!"',
            position: 'absolute',
            display: shouldShowRecapAlerts && hasSterilizationCertificateToValidate ? 'flex' : 'none',
            justifyContent: 'center',
            alignItems: 'center',
            bottom: 3,
            right: 4,
            backgroundColor: (theme) => theme.palette.warning.main,
            color: 'white',
            fontWeight: 700,
            borderRadius: '50%',
            width: 16,
            height: 16,
            fontSize: 10,
          },
        }}
      />
    </HtmlTooltip>
  )
}

export function getTabForAdoptionAttemptStatus(attempt: AdoptionAttemptDto): 'ongoing' | 'past' | 'suspended' {
  if (attempt.status === AdoptionAttemptDtoStatus.Done && attempt.step > 5) {
    return 'past'
  }
  if (
    attempt.status === AdoptionAttemptDtoStatus.SuspendedByCustomer ||
    attempt.status === AdoptionAttemptDtoStatus.SuspendedByUser
  ) {
    return 'suspended'
  }
  return 'ongoing'
}
